import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { API_BASE_URL } from '@/config';
import { useLoader } from "@/contexts/LoaderContext";
import { toast } from "sonner";
import {
    useDigioSdk,
    getDigioConstructor,
    resolveDigioToken,
    removeOrphanedDigioOverlays,
    fitDigioOverlayToViewport,
    type DigioCallbackResponse,
    type DigioEvent,
    type DigioInstance,
} from "@/hooks/useDigioSdk";
import { useMandateStatusWatcher } from "@/hooks/useMandateStatusWatcher";
import { DIGIO_ENVIRONMENT } from "@/config";

/**
 * Digio eNACH events we subscribe to (entity: `nach`).
 *
 * BOTH spellings of the UPI cancel event are listed on purpose. Digio's own
 * event table writes it as `nach_upi_mandate.cancelled_by_user` (underscore
 * where every sibling has a dot) — the same kind of upstream typo as their
 * misspelled `DOCUEMNT_NOT_READY_FOR_THIS_ACTION` error code. We cannot tell
 * from the SDK bundle which form actually goes over the wire, and the filter
 * is an exact-match test (`event_filter.includes(event.data.event)`), so a
 * single wrong guess would silently swallow every UPI cancellation. Subscribing
 * to both costs nothing; the handler already keys off `endsWith("cancelled_by_user")`.
 *
 * Read-only — pass a fresh mutable copy (`[...MANDATE_EVENTS]`) to the SDK.
 */
const MANDATE_EVENTS: readonly string[] = [
    "nach.upi_mandate.vpa_verified",
    "nach.upi_mandate.authenticated",
    "nach.upi_mandate.already_authenticated",
    "nach.upi_mandate.rejected",
    "nach.upi_mandate.failed",
    "nach_upi_mandate.cancelled_by_user", // verbatim from Digio's table (sic)
    "nach.upi_mandate.cancelled_by_user", // dotted form, in case the table is wrong
    "nach.api_mandate.authenticated",
    "nach.api_mandate.already_authenticated",
    "nach.api_mandate.failed",
    "nach.api_mandate.cancelled_by_user",
] as const;

interface NACHMandateProps {
    value?: any;
    onChange?: (data: any) => void;
    onDone?: () => void;
    isBuilderMode?: boolean;
    registerSubmit?: (submitFn: () => Promise<boolean>) => void;
    /** Controls whether the NACH section is active (UPI done or skipped) */
    isUpiCompleted?: boolean;
}

/**
 * NACH Mandate Component — either-mandate logic
 *
 * Uses the Digio Web SDK v11 inline iframe (no raw <iframe src=...>).
 * Passes digio_token_id as the 3rd arg to digio.submit() so the gateway
 * skips its OTP screen entirely.
 *
 * While Digio overlay is open: poll every 4s.
 * When Digio callback fires CANCELLED: keep polling for 90s (async bank confirmation).
 * After 90s with no success: check UPI status as final fallback.
 *   - UPI success → mark the page satisfied so Continue enables
 *   - Both fail   → stay on page, show error (no reload, no navigation)
 *
 * IMPORTANT: this component NEVER advances the journey. Success only reports
 * completed:true upward so JourneyPage enables its Continue button; the stage
 * moves forward exclusively when the user clicks Continue. A failure path must
 * never report completion, otherwise a refresh would skip the page.
 */
const NACHMandate: React.FC<NACHMandateProps> = ({
    value,
    onChange,
    onDone,
    isBuilderMode = false,
    registerSubmit,
    isUpiCompleted = true
}) => {
    const { loading, setLoading, setLoaderMessage } = useLoader();
    const { loaded: sdkLoaded } = useDigioSdk();
    const [fetching, setFetching] = useState(true);
    const [isCompleted, setIsCompleted] = useState(false);
    // True when the backend confirms UPI is already done on page load.
    // This unlocks the NACH section independently of the isUpiCompleted prop
    // (which starts as false in JourneyPage until the UPI component fires onDone).
    const [upiDoneFromBackend, setUpiDoneFromBackend] = useState(false);
    const [customerInfo, setCustomerInfo] = useState({
        identifier: "",
        bankingName: "",
        // IFSC entered on the Bank Details page. Display-only here — it is also
        // sent to Digio as destination_bank_id so the NACH form opens prefilled.
        ifsc: ""
    });

    // Refs for poll/countdown timers so we can clear them from any closure
    const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const isHandledRef = useRef(false);
    const postCloseSecondsRef = useRef(0);
    const hasLoadedRef = useRef(false); // Prevent duplicate API calls from React re-renders

    // Digio SDK instance ref — used to call cancel() on cleanup
    const digioRef = useRef<DigioInstance | null>(null);
    // runId guard — prevents phantom CANCELLED from a discarded Digio instance
    const runIdRef = useRef(0);
    // Cleanup returned by fitDigioOverlayToViewport — detaches resize listeners
    const overlayCleanupRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        // Prevent duplicate calls from React StrictMode or re-renders
        if (hasLoadedRef.current) return;
        hasLoadedRef.current = true;

        axios
            .post(`${API_BASE_URL}/api/get_verified_mandate_details/`)
            .then((res) => {
                if (res.status === 200) {
                    setCustomerInfo({
                        identifier: String(res.data.customer_identifier),
                        bankingName: res.data.cust_banking_name,
                        ifsc: res.data.bank_ifsc || ""
                    });
                    if (res.data.nach_mandate_done) {
                        // NACH already confirmed by Digio (status=4) on a previous session.
                        // Mark complete locally and fire onChange so JourneyPage sets
                        // localState[id].completed=true → mandateDone=true → Continue enabled.
                        setIsCompleted(true);
                        if (onChange) onChange({ completed: true });
                        toast.success("NACH Mandate already completed!");
                    }
                    // If UPI is already confirmed on the backend, unlock NACH immediately.
                    // JourneyPage.upiSkipped starts as false and is only set to true when
                    // the UPI component fires onDone() in the current session — it is never
                    // initialised from backend state. So when the user reloads the page
                    // (or arrives here with UPI already done), isUpiCompleted prop is false
                    // and the NACH button stays greyed out. Reading upi_mandate_done here
                    // and storing it in local state breaks that dependency.
                    if (res.data.upi_mandate_done) {
                        setUpiDoneFromBackend(true);
                    }
                }
            })
            .catch((err) => {
                console.error("Fetch error:", err);
                toast.error("Failed to fetch mandate details.");
            })
            .finally(() => setFetching(false));
    }, []);

    // ── Server-truth watcher ─────────────────────────────────────────────────
    // The bootstrap fetch above runs ONCE (hasLoadedRef + [] deps), so before
    // this existed the card could only learn about a confirmation that had
    // already happened by the time the component mounted. Anything that landed
    // afterwards — most importantly the background sweep
    // (los_app.poll_pending_mandates_task) confirming a mandate the customer
    // completed in their banking app — was invisible until a manual page reload.
    //
    // This watcher re-reads the server's view on an interval while the tab is
    // visible AND immediately when the tab regains focus, which is exactly the
    // moment the customer returns from the UPI / bank app. No reload needed.
    //
    // It only reports; it never advances the journey. On success it fires the
    // same onChange({completed:true}) the in-card poll uses, which flips
    // JourneyPage's mandateDone and turns Continue green.
    const { status: serverMandateStatus } = useMandateStatusWatcher({
        enabled: !isBuilderMode && !isCompleted,
        // Stop only when BOTH mandates have reached a final outcome (registered, or
        // rejected/revoked '3'). Stopping on `s.nach.done` alone was wrong: this
        // card also reads the UPI entry to decide whether to unlock itself, so a
        // still-pending UPI would have been frozen at its last polled value.
        stopWhen: (s) => {
            const settled = (m: { done: boolean; status: string | null }) =>
                m.done || m.status === '3';
            return settled(s.nach) && settled(s.upi);
        },
    });

    // "Registration has started but is not resolved yet": Digio has issued a
    // mandate_id, the mandate is not confirmed, and it has not terminally failed
    // (status '3' = rejected/revoked). Drives the in-progress indicator below so an
    // idle-looking card does not make the user think nothing happened — which is
    // exactly the trap after returning from the bank app with a dead poll loop.
    const nachServer = serverMandateStatus?.nach;
    // Any mandate_id that is not yet confirmed counts as "something happened here",
    // INCLUDING a rejected/revoked one (status '3'). A dead mandate still needs an
    // explanation on screen — rendering nothing is what made the card look like it
    // had never been touched, which the user reads as "kuch nahi hua".
    const nachInProgress = Boolean(nachServer?.mandate_id && !nachServer.done);
    // status '3' = rejected/revoked at Digio. Switches the wording and colour so we
    // never spin a hopeful blue spinner on a mandate that can never succeed.
    const nachFailed = nachServer?.status === '3';

    // UPI has reached a FINAL outcome server-side — either registered (done) or
    // rejected/revoked ('3'). Used to unlock this card: see isEffectivelyUnlocked.
    const upiServer = serverMandateStatus?.upi;
    const upiResolvedFromBackend = Boolean(
        upiServer && (upiServer.done || upiServer.status === '3'),
    );

    useEffect(() => {
        if (!serverMandateStatus) return;

        // NACH confirmed server-side (either by the in-card poll's own verify call
        // or, crucially, by the background sweep while we were not looking).
        // Defensive: read through the already-optional-chained locals rather than
        // serverMandateStatus.X.done. The hook normalises its payload, but this
        // guarantees a malformed/partial object can never crash the page the way an
        // empty 304 body did in production.
        if (nachServer?.done && !isCompleted) {
            setIsCompleted(true);
            if (onChange) onChange({ completed: true });
            toast.success("NACH Mandate confirmed!");
        }

        // Keep the UPI-derived unlock in sync too: if UPI got confirmed while this
        // page was backgrounded, the NACH section must unlock without a reload.
        if (upiServer?.done && !upiDoneFromBackend) {
            setUpiDoneFromBackend(true);
        }
        // onChange is intentionally omitted: JourneyPage passes an inline arrow, so
        // including it would re-run this effect on every parent render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverMandateStatus, isCompleted, upiDoneFromBackend]);

    // Clean up timers and Digio instance on unmount
    useEffect(() => {
        return () => {
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
            if (countdownRef.current) clearInterval(countdownRef.current);
            if (digioRef.current) {
                try { digioRef.current.cancel(); } catch { /* ignore */ }
                digioRef.current = null;
            }
            if (overlayCleanupRef.current) {
                overlayCleanupRef.current();
                overlayCleanupRef.current = null;
            }
        };
    }, []);

    /**
     * Checks UPI mandate status via the caprion wrapper endpoint.
     * Returns true if UPI is confirmed successful.
     * Used as a final fallback when NACH fails/times out.
     */
    const checkUpiMandateSuccess = async (): Promise<boolean> => {
        try {
            const res = await axios.post(`${API_BASE_URL}/api/caprion/digio/verify-upi-mandate/`);
            return res.data.is_success === true;
        } catch {
            return false;
        }
    };

    /**
     * Called when NACH succeeds.
     * Closes Digio overlay and marks NACH as completed so the parent's
     * Continue button turns green. Does NOT advance the journey.
     */
    const handleNachSuccess = useCallback(async () => {
        if (isHandledRef.current) return;
        isHandledRef.current = true;
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);

        if (digioRef.current) {
            try { digioRef.current.cancel(); } catch { /* ignore */ }
            digioRef.current = null;
        }
        removeOrphanedDigioOverlays();

        setLoading(true);
        setLoaderMessage("mandate");
        toast.success("NACH Mandate verified successfully!");
        setIsCompleted(true);
        if (onChange) onChange({ completed: true });
        if (onDone) onDone();
        setLoaderMessage(null);
        setLoading(false);
    }, [onChange, onDone]);

    /**
     * Called when NACH times out (90s after Digio overlay closes) with no success.
     * Checks UPI status as a final fallback:
     *   - UPI success → mark completed so Continue enables (no auto-advance)
     *   - Both fail   → stay on page, show error; completion is NOT reported, so
     *                   the recorded journey stage stays on this page and a
     *                   refresh cannot move the user forward.
     */
    const handleNachFailureCheckUpi = useCallback(async () => {
        if (isHandledRef.current) return;
        isHandledRef.current = true;
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);

        if (digioRef.current) {
            try { digioRef.current.cancel(); } catch { /* ignore */ }
            digioRef.current = null;
        }
        removeOrphanedDigioOverlays();

        setLoading(true);
        setLoaderMessage("mandate");
        toast.info("Checking UPI mandate status as fallback…");

        try {
            const upiSuccess = await checkUpiMandateSuccess();
            if (upiSuccess) {
                // A valid mandate exists (UPI). Report completion so the parent's
                // Continue button enables — but do NOT advance the journey here.
                toast.success("UPI Mandate confirmed! You can continue.");
                if (onChange) onChange({ completed: true });
            } else {
                // Neither NACH nor UPI succeeded — stay on page and report nothing,
                // so the journey stage is left exactly where it is.
                toast.error("NACH mandate could not be confirmed. Please try again.");
            }
        } catch {
            toast.error("Could not verify mandate status. Please try again.");
        } finally {
            setLoaderMessage(null);
            setLoading(false);
        }
    }, [onChange]);

    /**
     * Start polling the NACH verify endpoint - Sequential polling (wait for response before next request)
     * Called once after the Digio overlay opens (before callback fires).
     * The poll runs until isHandledRef is set (by success or failure handler).
     */
    const startPolling = useCallback((runId: number) => {
        const nachEndpoint = `${API_BASE_URL}/api/caprion/digio/verify-nach-mandate/`;
        
        const poll = async () => {
            if (isHandledRef.current || runId !== runIdRef.current) {
                if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
                return;
            }
            
            try {
                const statusCheck = await axios.post(nachEndpoint);
                if (statusCheck.data.is_success === true) {
                    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
                    await handleNachSuccess();
                    return;
                }
                // Still pending — schedule next poll after 4 seconds
                pollTimerRef.current = setTimeout(poll, 4000);
            } catch (_) {
                // Error — retry after 4 seconds
                pollTimerRef.current = setTimeout(poll, 4000);
            }
        };
        
        // Start first poll immediately
        poll();
    }, [handleNachSuccess]);

    /**
     * Called when the user SUBMITS the NACH form inside the Digio iframe
     * (Digio callback fires with no error_code, or event "authenticated").
     * NACH mandates are processed asynchronously — the bank may take minutes
     * to confirm. Poll for up to 5 minutes; if still not confirmed, fall back
     * to UPI check. Do NOT declare success here — only the poll can do that.
     */
    const handleDigioFormSubmitted = useCallback((runId: number) => {
        if (isHandledRef.current || runId !== runIdRef.current) return;

        // Poll for up to 5 minutes (300 ticks × 1s) for async bank confirmation.
        const MAX_POLL_SECONDS = 300;
        postCloseSecondsRef.current = MAX_POLL_SECONDS;
        setLoading(true);
        setLoaderMessage("mandate");
        toast.info("NACH form submitted. Waiting for bank confirmation…");

        countdownRef.current = setInterval(() => {
            if (isHandledRef.current) {
                if (countdownRef.current) clearInterval(countdownRef.current);
                return;
            }
            postCloseSecondsRef.current -= 1;
            if (postCloseSecondsRef.current <= 0) {
                if (countdownRef.current) clearInterval(countdownRef.current);
                if (pollTimerRef.current) clearInterval(pollTimerRef.current);
                // 5 minutes elapsed — check UPI as last resort
                void handleNachFailureCheckUpi();
            }
        }, 1000);
    }, [handleNachFailureCheckUpi]);

    /**
     * Called when Digio fires CANCELLED (user closed the overlay WITHOUT
     * completing the form). Keeps polling for 90s in case the bank confirms
     * asynchronously; if not confirmed → check UPI fallback.
     */
    const handleDigioCancelled = useCallback((runId: number) => {
        if (isHandledRef.current || runId !== runIdRef.current) return;

        const POST_CLOSE_POLL_SECONDS = 90;
        postCloseSecondsRef.current = POST_CLOSE_POLL_SECONDS;
        setLoading(true);
        setLoaderMessage("mandate");
        toast.info("Waiting for bank confirmation… please hold on.");

        countdownRef.current = setInterval(() => {
            if (isHandledRef.current) {
                if (countdownRef.current) clearInterval(countdownRef.current);
                return;
            }
            postCloseSecondsRef.current -= 1;
            if (postCloseSecondsRef.current <= 0) {
                if (countdownRef.current) clearInterval(countdownRef.current);
                if (pollTimerRef.current) clearInterval(pollTimerRef.current);
                void handleNachFailureCheckUpi();
            }
        }, 1000);
    }, [handleNachFailureCheckUpi]);

    const handleNachSubmit = async () => {
        setLoading(true);
        setLoaderMessage("mandate");

        // New attempt — invalidate callbacks from any previous Digio instance
        const runId = ++runIdRef.current;
        isHandledRef.current = false;
        postCloseSecondsRef.current = 0;
        if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);
        if (digioRef.current) {
            try { digioRef.current.cancel(); } catch { /* ignore */ }
            digioRef.current = null;
        }
        removeOrphanedDigioOverlays();

        // ── Initialise Digio SDK inline (must happen inside the click handler) ──
        const DigioCtor = getDigioConstructor();
        let digio: DigioInstance | null = null;
        let inlineReady = false;

        if (!sdkLoaded || !DigioCtor) {
            console.warn("[NACHMandate] Digio SDK not ready — will fall back to redirect_url.");
        } else {
            try {
                digio = new DigioCtor({
                    environment: DIGIO_ENVIRONMENT,
                    logo: `${window.location.origin}/logo.svg`,
                    theme: { primaryColor: "#065A4E", secondaryColor: "#000000" },
                    is_iframe: true,
                    callback: (response: DigioCallbackResponse) => {
                        console.log("[NACHMandate] Digio callback:", response);
                        if (runId !== runIdRef.current) {
                            console.warn("[NACHMandate] Ignoring stale Digio callback:", response);
                            return;
                        }
                        if (response?.error_code) {
                            if (response.error_code === "CANCELLED") {
                                // User closed the overlay — keep polling for async bank confirmation
                                handleDigioCancelled(runId);
                            } else {
                                // Hard error from Digio (not just a close)
                                void handleNachFailureCheckUpi();
                            }
                            return;
                        }
                        // Digio callback fires when the user SUBMITS the form in the iframe,
                        // NOT when the mandate is confirmed by the bank. NACH mandates are
                        // processed asynchronously — the backend verify endpoint must return
                        // is_success:true before we can declare success. Poll for up to 5 min.
                        console.log("[NACHMandate] Digio callback success (form submitted) — waiting for backend confirmation via poll.");
                        handleDigioFormSubmitted(runId);
                    },
                    event_listener: (event: DigioEvent) => {
                        if (runId !== runIdRef.current) return;
                        const name = event?.event ?? "";
                        console.log("[NACHMandate] Digio event:", name);
                        if (name.endsWith("authenticated") || name.endsWith("already_authenticated")) {
                            // Same as callback success — form submitted, mandate is async.
                            // Do NOT call handleNachSuccess here; let the poll confirm it.
                            console.log("[NACHMandate] Digio event authenticated — waiting for backend confirmation via poll.");
                            handleDigioFormSubmitted(runId);
                            return;
                        }
                        if (name.endsWith("cancelled_by_user")) {
                            handleDigioCancelled(runId);
                            return;
                        }
                        if (name.endsWith("rejected") || name.endsWith("failed")) {
                            void handleNachFailureCheckUpi();
                        }
                    },
                    // Fresh copy per instance — SDK mutates the array in place
                    event_filter: [...MANDATE_EVENTS],
                });
                digio.init();
                digioRef.current = digio;
                inlineReady = true;
                // Size the overlay to the visual viewport so it fits on mobile
                // (the SDK writes desktop-sized inline styles by default).
                if (overlayCleanupRef.current) overlayCleanupRef.current();
                overlayCleanupRef.current = fitDigioOverlayToViewport("NACH mandate");
            } catch (initError) {
                console.warn("[NACHMandate] Digio init() threw:", initError);
                digio = null;
                digioRef.current = null;
                removeOrphanedDigioOverlays();
            }
        }

        try {
            const payload = {
                customer_identifier: customerInfo.identifier,
                cust_banking_name: customerInfo.bankingName,
                vpa: ""
            };
            const response = await axios.post(`${API_BASE_URL}/api/create_digio_nach_mandate/`, payload);

            if (response.status !== 200 || !response.data) {
                throw new Error("Mandate setup could not be initiated.");
            }

            const mandateId: string | null = response.data.mandate_id ?? null;
            const identifier: string = response.data.customer_identifier || customerInfo.identifier;
            const redirectUrl: string | null = response.data.redirect_url || null;
            const tokenId = resolveDigioToken(
                response.data.digio_token_id,
                response.data.digio_token_valid_till,
                "NACH mandate",
            );

            if (inlineReady && digio && mandateId) {
                // ── SDK path: submit inline with token (skips OTP) ──────────────
                try {
                    digio.submit(mandateId, identifier, tokenId);
                } catch (submitError) {
                    console.warn("[NACHMandate] digio.submit() threw:", submitError);
                    // Fall through to redirect fallback below
                    inlineReady = false;
                }
            }

            if (!inlineReady || !digio || !mandateId) {
                // ── Fallback: open Digio's own authentication_url ───────────────
                if (digioRef.current) {
                    try { digioRef.current.cancel(); } catch { /* ignore */ }
                    digioRef.current = null;
                }
                removeOrphanedDigioOverlays();

                if (redirectUrl) {
                    console.warn("[NACHMandate] Falling back to redirect_url:", redirectUrl);
                    window.open(redirectUrl, "_blank", "noopener,noreferrer");
                    startPolling(runId);
                    setLoaderMessage(null);
                    setLoading(false);
                } else {
                    toast.error("Mandate setup could not be initiated. Please try again.");
                    setLoaderMessage(null);
                    setLoading(false);
                }
                return;
            }

            // SDK overlay is now covering the viewport — hide our loader
            startPolling(runId);
            setLoaderMessage(null);
            setLoading(false);

        } catch (err: any) {
            if (digioRef.current) {
                try { digioRef.current.cancel(); } catch { /* ignore */ }
                digioRef.current = null;
            }
            removeOrphanedDigioOverlays();
            toast.error(err.response?.data?.error || "An error occurred initiating NACH mandate.");
            setLoaderMessage(null);
            setLoading(false);
        }
    };

    if (fetching) return null;

    // Effective unlock: either the parent prop (UPI just completed in this session)
    // or the backend confirmed UPI was already done before this page loaded.
    //
    // upiResolvedFromBackend also unlocks on a FAILED UPI mandate (status '3').
    // UPI is optional and skippable, so a revoked/rejected UPI attempt must not
    // leave NACH permanently greyed out — that would dead-end the customer with no
    // way to register any mandate at all. Previously only a SUCCESSFUL UPI (or an
    // explicit in-session skip) unlocked this card, so a user whose UPI was revoked
    // and who then reloaded the page could not proceed.
    //
    // nachInProgress unlocks it too: if a NACH mandate_id already exists then this
    // card has ALREADY been used — the UPI gate is historical at that point. Leaving
    // it locked (the `pointer-events-none opacity-60` branch below) greys out a card
    // that is actively verifying, which looks broken and hides the status line the
    // user needs to read.
    const isEffectivelyUnlocked =
        isUpiCompleted || upiDoneFromBackend || upiResolvedFromBackend || nachInProgress;

    return (
        <div className={`border rounded-xl p-6 transition-all duration-500 relative ${
            !isEffectivelyUnlocked
                ? "border-gray-200 bg-gray-50 opacity-60 pointer-events-none"
                : isCompleted
                ? "border-intellend-green/20 bg-intellend-green/5 overflow-hidden"
                    : "border-intellend-green/30 bg-white shadow-sm"
        }`}>
            {isCompleted && (
                <div className="absolute inset-0 z-10 bg-white/60 backdrop-blur-[2px] rounded-xl flex items-center justify-center">
                    <div className="flex items-center gap-2 text-green-700 font-bold text-lg bg-white px-4 py-2 rounded-full shadow-sm border border-green-100">
                        <CheckCircle className="h-6 w-6 text-green-600" />
                        NACH Mandate Registered
                    </div>
                </div>
            )}

            {/* IN-PROGRESS CHIP — sits inline next to the heading.
                Shown once a mandate_id exists on the server but the mandate is not
                yet confirmed, i.e. registration HAS started and the background sweep
                is checking it. Without it the card looks completely idle in exactly
                the situation where the user is most likely to think nothing happened
                and start over (returned from the bank app, in-card poll loop died
                with the backgrounded tab).

                Deliberately NOT shown on terminal failure (status '3') — a dead
                mandate must not spin forever pretending to work. */}
            <h2 className={`text-xl font-bold text-gray-800 ${
                !isCompleted && nachInProgress ? "" : "mb-4"
            }`}>
                2. NACH Mandate
            </h2>

            {!isCompleted && nachInProgress && (
                nachFailed ? (
                    <p className="mt-1 mb-4 flex items-center gap-1.5 text-xs text-red-600">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                        Previous mandate failed — please set it up again.
                    </p>
                ) : (
                    <p className="mt-1 mb-4 flex items-center gap-1.5 text-xs text-blue-700">
                        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                        Verifying… mandate registered, confirming with your bank.
                    </p>
                )
            )}

            <div className="space-y-3 mb-6">
                <div>
                    <p className="text-sm font-semibold text-gray-700">Customer Identifier (Phone)</p>
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mt-1 text-gray-600">
                        {customerInfo.identifier || "-"}
                    </div>
                </div>
                <div>
                    <p className="text-sm font-semibold text-gray-700">Customer Banking Name</p>
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mt-1 text-gray-600">
                        {customerInfo.bankingName || "-"}
                    </div>
                </div>
                <div>
                    <p className="text-sm font-semibold text-gray-700">IFSC Code</p>
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 mt-1 text-gray-600">
                        {customerInfo.ifsc || "-"}
                    </div>
                </div>
            </div>

            <div className="space-y-4">
                <p className="text-sm text-gray-600">
                    Your bank account details will be automatically fetched for the NACH registration based on the information provided earlier.
                </p>
                <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg flex gap-3 text-sm text-amber-800">
                    <AlertCircle className="h-5 w-5 shrink-0" />
                    <p>By clicking "Setup Mandate", you agree to authorize automatic monthly repayments via NACH.</p>
                </div>
                <Button
                    type="button"
                    onClick={handleNachSubmit}
                    className="w-full bg-intellend-green hover:bg-intellend-green/90 h-12 text-lg font-bold shadow-md transition-all active:scale-[0.98]"
                    disabled={loading || !isEffectivelyUnlocked || isCompleted}
                >
                    {loading ? "Processing..." : "Setup NACH Mandate"}
                </Button>

                {/* NOTE: the "Change Bank Details" escape hatch is NOT rendered here.
                    JourneyPage renders it at the TOP of the eMandate page (above both
                    mandate cards) so it is reachable before the user starts and while
                    this card is still locked behind UPI. */}
            </div>

            <p className="text-center text-xs text-gray-400 mt-4 italic">Securely powered by Digio</p>
        </div>
    );
};

export default NACHMandate;
